import { parseHtmlString, serializeHtmlToString } from '../html-parse-serialize';
import getEditableTextFields, { TextFieldsData } from './editableTextFields/getData';
import saveEditableTextFields from './editableTextFields/saveData';
import getEditableMedia, { HtmlMediaElementConfig } from './editableMedia/getData';
import saveEditableMedia from './editableMedia/saveData';
import getEditableLinks, { LinkConfig } from '../editableLinks/getData';
import saveEditableLinks from '../editableLinks/saveData';
import { replaceVideosWithCoverImages } from '../videos';
import { EditableItem, editableId } from './constants';
import Utils from '../../../../utils';
import { EditorType } from '../../constants';

interface PageData {
	html: string; // used for rendering in editor
	defaultHtml: string; // used for saving changes on server
	fontsPath?: string;
	googleFontsPath?: string;
	editableTextFields: TextFieldsData;
	editableMedia: HtmlMediaElementConfig[];
	editableLinks: LinkConfig[];
	isError: boolean;
}

const getAllEditableElementsSelector = () => {
	const editableElementsSelectors = [
		`[${EditableItem.Text}]`,
		`[${EditableItem.Image}]`,
		`[${EditableItem.Video}]`,
	];

	return editableElementsSelectors.join(',');
};

const getEditableElements = (htmlDocument: Document): HTMLElement[] => ([
	...htmlDocument.querySelectorAll(getAllEditableElementsSelector()),
] as HTMLElement[]);

const isText = (element: HTMLElement) => element.hasAttribute(EditableItem.Text);
const isImage = (element: HTMLElement) => element.hasAttribute(EditableItem.Image);

export const isModifiedFromBackoffice = (htmlString: string): boolean => (
	htmlString.includes(editableId)
);

const setIdsToEditableElements = (htmlString: string): string => {
	const regExp = new RegExp(`${EditableItem.Text}|${EditableItem.Image}|${EditableItem.Video}`, 'g');
	return htmlString.replace(regExp, (match) => {
		const id = `edit-${Utils.generateId()}`;
		return `${match} ${editableId}="${id}"`;
	});
};

const removeEditableElements = (htmlDocument: Document): Document => {
	const elements = getEditableElements(htmlDocument);

	elements.forEach((element) => {
		if (isText(element)) {
			element.innerHTML = '';
		} else {
			const div = document.createElement('div');
			const type = isImage(element) ? EditableItem.Image : EditableItem.Video;
			div.setAttribute(editableId, element.getAttribute(editableId) as string);
			div.setAttribute(type, '');

			const styles = element.getAttribute('style');
			if (styles) {
				div.setAttribute('style', styles);
			}

			const classes = element.getAttribute('class');
			if (classes) {
				div.setAttribute('class', classes);
			}

			element.parentNode!.replaceChild(div, element);
		}
	});

	return htmlDocument;
};

const removeHrefFromLinks = (htmlDocument: Document): Document => {
	const linkElements = [...htmlDocument.querySelectorAll('a')];
	linkElements.forEach((element) => {
		element.removeAttribute('href');
	});
	return htmlDocument;
};

const getFontsPaths = (htmlDocument: Document): {
	fontsPath: string;
	googleFontsPath: string;
} => {
	// this path to fonts css file is hardcoded for now
	// and must be set in initial html document prepared by user
	const FONTS_URL = 'font/fonts.css';
	const GOOGLE_FONTS_URL = 'fonts.googleapis.com';
	const linkElements = [...htmlDocument.head.querySelectorAll('link')];
	const fontsLinkElement = linkElements.find(({ href }) => href.includes(FONTS_URL));
	const googleFontsLinkElement = linkElements.find(({ href }) => href.includes(GOOGLE_FONTS_URL));
	const fontsPath = fontsLinkElement ? FONTS_URL : '';
	const googleFontsPath = googleFontsLinkElement ? googleFontsLinkElement.href : '';

	return {
		fontsPath,
		googleFontsPath,
	};
};

export const getModifiedPageData = (
	page: number,
	htmlString: string,
	isEditablePage: boolean,
	isAccountManagerOrAdmin: boolean,
): PageData => {
	const pageData = {
		html: htmlString,
		defaultHtml: htmlString,
		isError: false,
	} as PageData;

	// try catch block is used to avoid app breaking in case something went wrong in parsing of html document
	try {
		let defaultHtmlString = htmlString; // used as default html for getting data from editable elements

		if (isEditablePage && !isModifiedFromBackoffice(htmlString)) {
			defaultHtmlString = setIdsToEditableElements(htmlString);
		}

		let htmlDocument = parseHtmlString(defaultHtmlString);
		let defaultHtmlDocument = parseHtmlString(defaultHtmlString);

		if (isEditablePage) {
			htmlDocument = removeEditableElements(htmlDocument);
			htmlDocument = removeHrefFromLinks(htmlDocument);
		} else {
			htmlDocument = replaceVideosWithCoverImages(htmlDocument);
		}

		pageData.html = serializeHtmlToString(htmlDocument);
		pageData.defaultHtml = serializeHtmlToString(defaultHtmlDocument);

		if (isEditablePage) {
			const { fontsPath, googleFontsPath } = getFontsPaths(defaultHtmlDocument);
			const editableTextFields = getEditableTextFields(page, defaultHtmlDocument, isModifiedFromBackoffice(htmlString));
			const editableMedia = getEditableMedia(page, defaultHtmlDocument);
			const editableLinks = getEditableLinks(page, defaultHtmlDocument, EditorType.HTML, [], isAccountManagerOrAdmin);
			pageData.fontsPath = fontsPath;
			pageData.googleFontsPath = googleFontsPath;
			pageData.editableTextFields = editableTextFields;
			pageData.editableMedia = editableMedia.items;
			pageData.editableLinks = editableLinks.items;
			pageData.isError = editableTextFields.isError || editableMedia.isError || editableLinks.isError;
		}
	} catch (error) {
		pageData.isError = true;
	}

	return pageData;
};

export const getPageHtmlToSave = (htmlString: string, { editableTextFields, editableMedia, editableLinks }): string => {
	const htmlDocument = parseHtmlString(htmlString);
	saveEditableTextFields(htmlDocument, editableTextFields);
	saveEditableMedia(htmlDocument, editableMedia);
	saveEditableLinks(htmlDocument, editableLinks, EditorType.HTML);

	return serializeHtmlToString(htmlDocument);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/htmlEditor/html.ts